/** Fasta bredder på ett 12-kolumners rutnät. Användaren ser namn, inte siffran 12. */
export const COL_SPANS = [12, 8, 6, 4] as const;
export type ColSpan = (typeof COL_SPANS)[number];

/**
 * Fasta höjder i radenheter (en enhet är 5.5rem).
 * Steget väljs efter hur mycket widgeten ska visa, inte efter innehållets råhöjd.
 */
export const ROW_SPANS = [2, 3, 4, 5, 6, 7] as const;
export type RowSpan = (typeof ROW_SPANS)[number];

export interface GridWidgetSize {
  colSpan: ColSpan;
  rowSpan: RowSpan;
}

export interface DashboardGridWidget {
  id: string;
  widgetType: string;
  column: number;
  order: number;
  colSpan?: number;
  rowSpan?: number;
  config?: Record<string, unknown>;
}

/** Bredd 4 behåller dagens tre kolumner. Höjden följer hur mycket widgeten visar. */
const DEFAULT_SIZE: Record<string, GridWidgetSize> = {
  'vehicle-count': { colSpan: 4, rowSpan: 3 },
  'vehicle-status': { colSpan: 4, rowSpan: 3 },
  'service-cost': { colSpan: 4, rowSpan: 5 },
  'production-cost': { colSpan: 12, rowSpan: 6 },
  'inspection-due': { colSpan: 4, rowSpan: 4 },
  'leasing-expiring': { colSpan: 4, rowSpan: 4 },
  'tax-due': { colSpan: 4, rowSpan: 4 },
  'unplugged-vehicles': { colSpan: 4, rowSpan: 4 },
  'low-battery': { colSpan: 4, rowSpan: 4 },
  'device-health': { colSpan: 4, rowSpan: 3 },
  'service-due': { colSpan: 4, rowSpan: 4 },
  'action-required': { colSpan: 8, rowSpan: 3 },
  'my-assignments': { colSpan: 4, rowSpan: 4 },
  mine: { colSpan: 4, rowSpan: 3 },
  'my-bookings': { colSpan: 12, rowSpan: 7 },
  'gps-data-lag': { colSpan: 4, rowSpan: 4 },
};

const FALLBACK_SIZE: GridWidgetSize = { colSpan: 4, rowSpan: 4 };

const WIDGET_REPLACEMENTS: { type: string; replaces: ReadonlySet<string>; placeAfter?: string }[] = [
  { type: 'device-health', replaces: new Set(['low-battery', 'unplugged-vehicles', 'gps-data-lag']) },
  {
    type: 'action-required',
    replaces: new Set(['service-due', 'inspection-due', 'leasing-expiring', 'tax-due']),
    placeAfter: 'vehicle-count',
  },
  { type: 'mine', replaces: new Set(['my-assignments']) },
];

function collapseOne<T extends DashboardGridWidget>(
  widgets: T[],
  replacement: { type: string; replaces: ReadonlySet<string>; placeAfter?: string },
): T[] {
  const firstIndex = widgets.findIndex((widget) => replacement.replaces.has(widget.widgetType));
  if (firstIndex < 0) return widgets;
  const withoutOld = widgets.filter((widget) => !replacement.replaces.has(widget.widgetType));
  if (withoutOld.some((widget) => widget.widgetType === replacement.type)) return withoutOld;
  const source = widgets[firstIndex];
  const size = defaultWidgetSize(replacement.type);
  const combined = {
    ...source,
    widgetType: replacement.type,
    colSpan: size.colSpan,
    rowSpan: size.rowSpan,
  } as T;
  const anchor = replacement.placeAfter
    ? withoutOld.findIndex((widget) => widget.widgetType === replacement.placeAfter)
    : -1;
  const insertAt = anchor >= 0
    ? anchor + 1
    : widgets.slice(0, firstIndex).filter((widget) => !replacement.replaces.has(widget.widgetType)).length;
  if (anchor >= 0) combined.order = withoutOld[anchor].order;
  return [...withoutOld.slice(0, insertAt), combined, ...withoutOld.slice(insertAt)];
}

/** Ersatta kort visas som ett: enhetshälsa, och åtgärdskortet för service, besiktning, leasing och skatt. */
export function collapseReplacedDashboardWidgets<T extends DashboardGridWidget>(widgets: T[]): T[] {
  return WIDGET_REPLACEMENTS.reduce((current, replacement) => collapseOne(current, replacement), widgets);
}

export function defaultWidgetSize(widgetType: string): GridWidgetSize {
  return DEFAULT_SIZE[widgetType] ?? FALLBACK_SIZE;
}

export function clampColSpan(value: unknown): ColSpan {
  const n = Number(value);
  return (COL_SPANS as readonly number[]).includes(n) ? (n as ColSpan) : FALLBACK_SIZE.colSpan;
}

/** Sparad höjd 6 på Mina bokningar var det gamla taket, inte ett kortare val. */
function resolvedRowSpan(widget: DashboardGridWidget): RowSpan {
  const size = defaultWidgetSize(widget.widgetType);
  if (widget.rowSpan == null) return size.rowSpan;
  const clamped = clampRowSpan(widget.rowSpan);
  if (widget.widgetType === 'my-bookings' && clamped === 6) return 7;
  // 4, 5 och den för höga 7 blir ett steg lägre. Valt 6 stannar.
  if (widget.widgetType === 'production-cost' && (clamped === 4 || clamped === 5 || clamped === 7)) return 6;
  return clamped;
}

export function clampRowSpan(value: unknown): RowSpan {
  const n = Number(value);
  return (ROW_SPANS as readonly number[]).includes(n) ? (n as RowSpan) : FALLBACK_SIZE.rowSpan;
}

/**
 * Gamla layouter har bara column/order. De packas radvis (order, sedan kolumn)
 * och får typens standardhöjd. Sparad bredd och höjd behålls.
 */
export function normalizeDashboardWidgets<T extends DashboardGridWidget>(widgets: T[]): T[] {
  const legacy = widgets.some((widget) => widget.colSpan == null);
  const sorted = collapseReplacedDashboardWidgets(widgets).sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    if (legacy) return a.column - b.column;
    return 0;
  });

  return sorted.map((widget, index) => {
    const size = defaultWidgetSize(widget.widgetType);
    return {
      ...widget,
      order: index,
      colSpan: widget.colSpan == null ? size.colSpan : clampColSpan(widget.colSpan),
      rowSpan: resolvedRowSpan(widget),
    };
  });
}
